import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import {
  PlusCircle,
  Trash2,
  Upload,
  Loader2,
  ImagePlus,
  Smile,
  Info,
  CheckCircle2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppLayout } from '@/components/AppLayout';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { LoginArea } from '@/components/auth/LoginArea';
import { cn } from '@/lib/utils';

interface EmojiEntry {
  id: string;
  shortcode: string;
  url: string;
  file?: File;
  preview?: string;
  uploading?: boolean;
  error?: string;
}

function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9_\s-]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export default function CreatePackPage() {
  useSeoMeta({
    title: 'Create Emoji Pack — EmojiVerse',
    description: 'Build and publish your own custom emoji pack to the Nostr network.',
    ogType: 'website',
    ogSiteName: 'EmojiVerse',
    ogTitle: 'Create Emoji Pack — EmojiVerse',
    ogDescription: 'Build and publish your own custom emoji pack to the Nostr network.',
    ogUrl: 'https://emojiverse.shakespeare.wtf/create',
    ogImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    ogImageWidth: '1536',
    ogImageHeight: '1024',
    ogImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Create Emoji Pack — EmojiVerse',
    twitterDescription: 'Build and publish your own custom emoji pack to the Nostr network.',
    twitterImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    twitterImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
  });

  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [packImage, setPackImage] = useState('');
  const [dTag, setDTag] = useState('');
  const [dTagManual, setDTagManual] = useState(false);
  const [emojis, setEmojis] = useState<EmojiEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!dTagManual) {
      setDTag(slugify(val));
    }
  };

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const [[, url]] = await uploadFile(file);
      setPackImage(url);
    } catch {
      toast({ title: 'Upload failed', description: 'Failed to upload cover image.', variant: 'destructive' });
    } finally {
      setUploadingCover(false);
    }
  };

  const handleEmojiFiles = async (files: FileList | null) => {
    if (!files) return;
    const newEntries: EmojiEntry[] = Array.from(files).map((file) => ({
      id: Math.random().toString(36).slice(2),
      shortcode: file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_'),
      url: '',
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
    }));
    setEmojis((prev) => [...prev, ...newEntries]);
  };

  const uploadEmoji = async (id: string) => {
    const entry = emojis.find((e) => e.id === id);
    if (!entry?.file || entry.url) return;

    setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, uploading: true, error: undefined } : e));
    try {
      const [[, url]] = await uploadFile(entry.file);
      setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, url, uploading: false } : e));
    } catch {
      setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, uploading: false, error: 'Upload failed' } : e));
    }
  };

  const uploadAllEmojis = async () => {
    const pending = emojis.filter((e) => !e.url && e.file);
    for (const emoji of pending) {
      await uploadEmoji(emoji.id);
    }
  };

  const updateEmoji = (id: string, field: 'shortcode' | 'url', value: string) => {
    setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));
  };

  const removeEmoji = (id: string) => {
    setEmojis((prev) => prev.filter((e) => e.id !== id));
  };

  const addManualEmoji = () => {
    setEmojis((prev) => [
      ...prev,
      { id: Math.random().toString(36).slice(2), shortcode: '', url: '' },
    ]);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: 'Title required', description: 'Please give your pack a name.', variant: 'destructive' });
      return;
    }
    if (!dTag.trim()) {
      toast({ title: 'Identifier required', description: 'Please set a pack identifier.', variant: 'destructive' });
      return;
    }
    const validEmojis = emojis.filter((e) => e.shortcode && e.url);
    if (validEmojis.length === 0) {
      toast({ title: 'No emojis', description: 'Please add at least one emoji to the pack.', variant: 'destructive' });
      return;
    }
    const pendingUploads = emojis.filter((e) => e.file && !e.url);
    if (pendingUploads.length > 0) {
      toast({ title: 'Upload pending', description: 'Please upload all emoji images first.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const tags: string[][] = [
        ['d', dTag],
        ['title', title.trim()],
      ];
      if (description.trim()) tags.push(['description', description.trim()]);
      if (packImage) tags.push(['image', packImage]);
      for (const emoji of validEmojis) {
        tags.push(['emoji', emoji.shortcode, emoji.url]);
      }

      const event = await publish({ kind: 30030, content: '', tags });

      const naddr = nip19.naddrEncode({
        kind: 30030,
        pubkey: user!.pubkey,
        identifier: dTag,
      });

      toast({ title: '🎉 Pack published!', description: `"${title}" is now live on Nostr.` });
      navigate(`/${naddr}`);
    } catch (err) {
      toast({ title: 'Publish failed', description: 'Could not publish your pack. Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <div className="text-7xl mb-6">✏️</div>
          <h2 className="text-2xl font-bold text-white mb-3">Sign in to create packs</h2>
          <p className="text-white/50 mb-8 max-w-sm">
            Log in with your Nostr account to create and publish emoji packs to the network.
          </p>
          <LoginArea className="max-w-xs" />
        </div>
      </AppLayout>
    );
  }

  const readyCount = emojis.filter((e) => e.url && e.shortcode).length;
  const pendingCount = emojis.filter((e) => e.file && !e.url).length;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <PlusCircle className="w-8 h-8 text-violet-400" />
            Create Emoji Pack
          </h1>
          <p className="text-white/50 mt-1">Build and publish a custom emoji pack to the Nostr network</p>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Pack Info */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Info className="w-4 h-4 text-violet-400" />
              Pack Details
            </h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-white/70">Pack Name *</Label>
                <Input
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="My Awesome Emojis"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-violet-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">
                  Identifier *
                  <span className="text-white/30 text-xs ml-1">(unique slug)</span>
                </Label>
                <Input
                  value={dTag}
                  onChange={(e) => { setDTag(slugify(e.target.value)); setDTagManual(true); }}
                  placeholder="my_awesome_emojis"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-violet-400 font-mono text-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A brief description of your emoji pack..."
                className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-violet-400 resize-none"
                rows={2}
              />
            </div>

            {/* Cover image */}
            <div className="space-y-2">
              <Label className="text-white/70">Cover Image</Label>
              <div className="flex items-center gap-4">
                {packImage ? (
                  <div className="relative group">
                    <img
                      src={packImage}
                      alt="Pack cover"
                      className="w-20 h-20 rounded-xl object-cover border border-white/20"
                    />
                    <button
                      onClick={() => setPackImage('')}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => coverInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-violet-400 hover:bg-white/5 transition-all"
                  >
                    {uploadingCover ? (
                      <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
                    ) : (
                      <ImagePlus className="w-5 h-5 text-white/40" />
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-white/50 text-sm">Upload a cover image or paste a URL</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-lg text-xs gap-1.5"
                    >
                      <Upload className="w-3 h-3" />
                      Upload
                    </Button>
                    <Input
                      value={packImage}
                      onChange={(e) => setPackImage(e.target.value)}
                      placeholder="https://..."
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 text-xs h-8"
                    />
                  </div>
                </div>
              </div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleCoverUpload(e.target.files[0])}
              />
            </div>
          </div>

          {/* Emojis section */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Smile className="w-4 h-4 text-violet-400" />
                Emojis
                {emojis.length > 0 && (
                  <span className="text-white/30 text-sm font-normal">
                    ({readyCount} ready{pendingCount > 0 ? `, ${pendingCount} pending upload` : ''})
                  </span>
                )}
              </h2>
              <div className="flex gap-2">
                {pendingCount > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={uploadAllEmojis}
                    className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded-full text-xs gap-1.5"
                  >
                    <Upload className="w-3 h-3" />
                    Upload All ({pendingCount})
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addManualEmoji}
                  className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-full text-xs gap-1.5"
                >
                  <PlusCircle className="w-3 h-3" />
                  Add URL
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-violet-500 hover:bg-violet-400 text-white rounded-full text-xs gap-1.5"
                >
                  <Upload className="w-3 h-3" />
                  Upload Files
                </Button>
              </div>
            </div>

            {/* Drop zone */}
            {emojis.length === 0 && (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/15 rounded-xl p-12 text-center cursor-pointer hover:border-violet-400 hover:bg-white/5 transition-all group"
              >
                <div className="text-5xl mb-3 group-hover:scale-110 transition-transform">🎨</div>
                <p className="text-white/50 font-medium mb-1">Drop emoji images here</p>
                <p className="text-white/30 text-sm">or click to browse — PNG, GIF, SVG, WebP</p>
              </div>
            )}

            {/* Emoji list */}
            {emojis.length > 0 && (
              <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                {emojis.map((emoji) => (
                  <div
                    key={emoji.id}
                    className={cn(
                      'flex items-center gap-3 rounded-xl p-3 border transition-all',
                      emoji.url
                        ? 'bg-green-500/5 border-green-500/20'
                        : emoji.error
                          ? 'bg-red-500/5 border-red-500/20'
                          : 'bg-white/5 border-white/10',
                    )}
                  >
                    {/* Preview */}
                    <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {emoji.preview || emoji.url ? (
                        <img
                          src={emoji.preview || emoji.url}
                          alt={emoji.shortcode}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Smile className="w-6 h-6 text-white/20" />
                      )}
                    </div>

                    {/* Shortcode */}
                    <div className="flex-1 min-w-0">
                      <Input
                        value={emoji.shortcode}
                        onChange={(e) => updateEmoji(emoji.id, 'shortcode', e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
                        placeholder="shortcode"
                        className="bg-white/10 border-white/10 text-white placeholder:text-white/20 font-mono text-sm h-8 mb-1"
                      />
                      <Input
                        value={emoji.url}
                        onChange={(e) => updateEmoji(emoji.id, 'url', e.target.value)}
                        placeholder="https://... (image URL)"
                        className="bg-white/5 border-white/10 text-white/60 placeholder:text-white/20 text-xs h-7"
                      />
                    </div>

                    {/* Status / actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {emoji.file && !emoji.url && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => uploadEmoji(emoji.id)}
                          disabled={emoji.uploading}
                          className="h-8 px-3 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-xs"
                        >
                          {emoji.uploading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Upload className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      )}
                      {emoji.url && (
                        <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                      )}
                      {emoji.error && (
                        <span className="text-red-400 text-xs">{emoji.error}</span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmoji(emoji.id)}
                        className="h-8 w-8 p-0 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {emojis.length > 0 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 rounded-xl border border-dashed border-white/15 text-white/30 text-sm hover:border-violet-400 hover:text-violet-400 transition-all"
              >
                + Add more files
              </button>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-white/30 text-sm">
              {readyCount > 0 ? `${readyCount} emojis ready to publish` : 'Add emojis to continue'}
            </p>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !title || !dTag || readyCount === 0}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white rounded-full px-8 shadow-lg shadow-violet-500/30 gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Smile className="w-4 h-4" />
                  Publish Pack
                </>
              )}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleEmojiFiles(e.target.files)}
        />
      </div>
    </AppLayout>
  );
}
